import React, { useState } from 'react'
import { useSupabaseVideoUpload } from '../../hooks/useSupabaseVideoUpload'

interface VideoManagerProps {
  onClose: () => void
}

const VideoManager: React.FC<VideoManagerProps> = ({ onClose }) => {
  const { videos, isLoading, uploadVideo, deleteVideo, activateVideo } = useSupabaseVideoUpload()
  const [isUploading, setIsUploading] = useState(false)
  const [uploadForm, setUploadForm] = useState({
    title: 'Video de la tienda',
    description: 'Video promocional de nuestra tienda'
  })
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)
  const [deleteToConfirm, setDeleteToConfirm] = useState<{ id: number; storagePath: string } | null>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validar tipo de archivo
      const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/mov']
      if (!allowedTypes.includes(file.type)) {
        setUploadError('Tipo de archivo no válido. Solo se permiten videos (MP4, WebM, OGG, AVI, MOV)')
        return
      }

      // Validar tamaño (máximo 50MB)
      const maxSize = 50 * 1024 * 1024 // 50MB
      if (file.size > maxSize) {
        setUploadError('El archivo es demasiado grande. Máximo 50MB')
        return
      }

      setSelectedFile(file)
      setUploadError(null)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Por favor selecciona un archivo de video')
      return
    }

    setIsUploading(true)
    setUploadError(null)
    setUploadSuccess(null)

    try {
      const result = await uploadVideo(selectedFile, uploadForm.title, uploadForm.description)
      
      if (result.success) {
        setUploadSuccess('Video subido exitosamente')
        setSelectedFile(null)
        setUploadForm({
          title: 'Video de la tienda',
          description: 'Video promocional de nuestra tienda'
        })
        // Limpiar el input de archivo
        const fileInput = document.getElementById('video-file') as HTMLInputElement
        if (fileInput) fileInput.value = ''
      } else {
        setUploadError(result.error || 'Error al subir el video')
      }
    } catch (error) {
      setUploadError('Error inesperado al subir el video')
    } finally {
      setIsUploading(false)
    }
  }

  const handleDelete = (id: number, storagePath: string) => {
    setDeleteToConfirm({ id, storagePath })
  }

  const confirmDelete = async () => {
    if (deleteToConfirm) {
      const result = await deleteVideo(deleteToConfirm.id, deleteToConfirm.storagePath)
      if (!result.success) {
        setUploadError('Error al eliminar el video: ' + result.error)
      } else {
        setUploadSuccess('Video eliminado exitosamente')
        setTimeout(() => setUploadSuccess(null), 3000)
      }
      setDeleteToConfirm(null)
    }
  }

  const cancelDelete = () => {
    setDeleteToConfirm(null)
  }

  const handleActivate = async (id: number) => {
    const result = await activateVideo(id)
    if (result.success) {
      setUploadSuccess('Video activado exitosamente')
      setTimeout(() => setUploadSuccess(null), 3000)
    } else {
      setUploadError('Error al activar el video: ' + result.error)
    }
  }

  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url)
    setUploadSuccess('URL copiada al portapapeles')
    setTimeout(() => setUploadSuccess(null), 2000)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Gestión de Videos de la Tienda</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Formulario de subida */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Subir Nuevo Video</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título del Video
                </label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-verano-primary focus:border-verano-primary"
                  placeholder="Título del video"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <input
                  type="text"
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-verano-primary focus:border-verano-primary"
                  placeholder="Descripción del video"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Archivo de Video
              </label>
              <input
                id="video-file"
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-verano-primary focus:border-verano-primary"
              />
              <p className="text-xs text-gray-500 mt-1">
                Formatos permitidos: MP4, WebM, OGG, AVI, MOV. Máximo 50MB.
              </p>
              {selectedFile && (
                <div className="mt-2 p-2 bg-blue-50 rounded-md">
                  <p className="text-sm text-blue-800">
                    <strong>Archivo seleccionado:</strong> {selectedFile.name} ({formatFileSize(selectedFile.size)})
                  </p>
                </div>
              )}
            </div>

            {uploadError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">{uploadError}</p>
              </div>
            )}

            {uploadSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800">{uploadSuccess}</p>
              </div>
            )}

            <button
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              className="bg-verano-primary text-white px-6 py-2 rounded-md hover:bg-verano-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Subiendo...
                </>
              ) : (
                'Subir Video'
              )}
            </button>
          </div>

          {/* Lista de videos */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Videos Existentes</h3>
            
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-verano-primary mx-auto"></div>
                <p className="mt-2 text-gray-600">Cargando videos...</p>
              </div>
            ) : videos.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No hay videos subidos aún.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {videos.map((video) => (
                  <div key={video.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <video
                      controls
                      preload="metadata"
                      className="w-full h-48 bg-black object-cover"
                      src={video.video_url}
                    />
                    <div className="p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-gray-800">{video.title}</h4>
                        {video.is_active && (
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                            Activo
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{video.description}</p>
                      <p className="text-xs text-gray-500 mb-4">Subido: {formatDate(video.created_at)}</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => copyToClipboard(video.video_url)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                        >
                          Copiar URL
                        </button>
                        {!video.is_active && (
                          <button
                            onClick={() => handleActivate(video.id)}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                          >
                            Activar
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(video.id, video.storage_path)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteToConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Confirmar eliminación</h3>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar este video? Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDelete}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VideoManager
